import { useState, useEffect } from 'react';
import turmasService from '../services/turmasService';
import { lancamentosService } from '../services/lancamentosService';
import Layout from '../components/Layout';
import styles from './MensalidadesPage.module.css';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import NFSeDetailsModal from '../components/NFSeDetailsModal';

interface Lancamento {
  id: number;
  descricao: string;
  valor: number;
  valorDesconto: number;
  valorFinal: number;
  dataVencimento: string;
  status: string;
  numero_parcela: number;
  total_parcelas: number;
  contratoId: number;
  tipoCobrancaId: number;
  tipoCobranca: { id: number; nome: string };
  numeroNFSE?: string;
  numeroRPS?: string;
  nfseId?: number;
  dataEmissaoNFSE?: string;
}

interface AlunoLancamentos {
  alunoId: number | null;
  alunoNome: string;
  responsavelNome?: string | null;
  lancamentos: Lancamento[];
}

export default function MensalidadesPage() {
  const [turmas, setTurmas] = useState<any[]>([]);
  const [selectedTurmaId, setSelectedTurmaId] = useState<number | null>(null);
  const [lancamentos, setLancamentos] = useState<AlunoLancamentos[]>([]);
  const [loading, setLoading] = useState(false);
  const [updatingLancamentos, setUpdatingLancamentos] = useState<Set<number>>(new Set());
  const [emitindoNFSe, setEmitindoNFSe] = useState<Set<number>>(new Set());
  const [emitindoMassa, setEmitindoMassa] = useState(false);
  const [progressoMassa, setProgressoMassa] = useState<{
    total: number;
    processados: number;
    sucesso: number;
    falha: number;
  } | null>(null);
  const [emitindoTodasTurmas, setEmitindoTodasTurmas] = useState(false);

  const { user } = useAuth();
  const isAdmin = user?.tipo === 'admin';

  const [modalData, setModalData] = useState<{
    isOpen: boolean;
    lancamento: any;
  }>({
    isOpen: false,
    lancamento: null,
  });

  const handleOpenModal = (lancamento: Lancamento) => {
    setModalData({
      isOpen: true,
      lancamento: lancamento,
    });
  };

  const handleCloseModal = () => {
    setModalData({
      isOpen: false,
      lancamento: null,
    });
  };

  const handleCancelNFSe = async (lancamentoId: number, motivo: string) => {
    setLoading(true);
    try {
      await lancamentosService.cancelarNFSE(lancamentoId, motivo);
      toast.success('NFSe cancelada com sucesso!');
      handleCloseModal();
      if (selectedTurmaId) {
        await loadLancamentos(selectedTurmaId);
      }
    } catch (error: any) {
      console.error('Erro ao cancelar NFSe:', error);
      const errorMessage = error.response?.data?.message || 'Erro ao cancelar NFSe';
      toast.error(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateNFSe = async (lancamentoId: number, dadosNFSE: { numeroNFSE?: string; numeroRPS?: string; nfseId?: number; dataEmissao?: string }) => {
    setLoading(true);
    try {
      await lancamentosService.updateNFSE(lancamentoId, dadosNFSE);
      toast.success('NFSe atualizada com sucesso!');
      handleCloseModal();
      if (selectedTurmaId) {
        await loadLancamentos(selectedTurmaId);
      }
    } catch (error: any) {
      console.error('Erro ao atualizar NFSe:', error);
      toast.error('Erro ao atualizar dados da NFSe');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTurmas();
  }, []);

  useEffect(() => {
    if (selectedTurmaId) {
      loadLancamentos(selectedTurmaId);
    }
  }, [selectedTurmaId]);

  const loadTurmas = async () => {
    try {
      const data = await turmasService.findAll();
      setTurmas(data);
      if (data.length > 0) {
        setSelectedTurmaId(data[0].id);
      }
    } catch (error) {
      console.error('Erro ao carregar turmas:', error);
    }
  };

  const loadLancamentos = async (turmaId: number) => {
    setLoading(true);
    try {
      const data = await lancamentosService.getByTurma(turmaId);
      setLancamentos(data);
    } catch (error) {
      console.error('Erro ao carregar lançamentos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (lancamentoId: number, novoStatus: string) => {
    const updating = new Set(updatingLancamentos);
    updating.add(lancamentoId);
    setUpdatingLancamentos(updating);

    try {
      await lancamentosService.updateStatus(lancamentoId, novoStatus);
      // Recarregar dados
      if (selectedTurmaId) {
        await loadLancamentos(selectedTurmaId);
      }
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
    } finally {
      updating.delete(lancamentoId);
      setUpdatingLancamentos(updating);
    }
  };

  const handleEmitirNFSe = async (lancamentoId: number) => {
    const emitting = new Set(emitindoNFSe);
    emitting.add(lancamentoId);
    setEmitindoNFSe(emitting);
    try {
      await lancamentosService.emitirNFSE(lancamentoId);
      toast.success('NFSe emitida com sucesso!');
      if (selectedTurmaId) {
        await loadLancamentos(selectedTurmaId);
      }
    } catch (error: any) {
      console.error('Erro ao emitir NFSe:', error);
      const errorMessage = error.response?.data?.message || 'Erro ao emitir NFSe';
      toast.error(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
    } finally {
      emitting.delete(lancamentoId);
      setEmitindoNFSe(new Set(emitting));
    }
  };

  const handleEmitirNFSeTodasTurmas = async () => {
    if (!confirm('Emitir NFSe de TODAS as turmas? Serão processados todos os lançamentos pagos sem NFSe de qualquer turma.')) return;
    setEmitindoTodasTurmas(true);
    try {
      const resultado = await lancamentosService.emitirNFSeTodasTurmas();
      if (resultado.falha > 0) {
        toast.error(`Concluído com falhas. Sucesso: ${resultado.sucesso}, Falhas: ${resultado.falha} (de ${resultado.total})`);
      } else {
        toast.success(`${resultado.sucesso} NFSe emitida(s) com sucesso de ${resultado.total} elegíveis`);
      }
      if (selectedTurmaId) await loadLancamentos(selectedTurmaId);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Erro ao emitir NFSe de todas as turmas');
    } finally {
      setEmitindoTodasTurmas(false);
    }
  };

  const handleEmitirNFSeEmMassa = async () => {
    const elegiveis = lancamentos
      .flatMap((alunoLanc) => alunoLanc.lancamentos)
      .filter((l) => l.status === 'pago' && !l.numeroNFSE);

    if (elegiveis.length === 0) {
      toast('Nenhum lancamento pago sem NFSe para processar');
      return;
    }

    setEmitindoMassa(true);
    setProgressoMassa({
      total: elegiveis.length,
      processados: 0,
      sucesso: 0,
      falha: 0,
    });

    let sucesso = 0;
    let falha = 0;

    for (let i = 0; i < elegiveis.length; i++) {
      const lancamento = elegiveis[i];
      try {
        await lancamentosService.emitirNFSE(lancamento.id);
        sucesso += 1;
      } catch (error: any) {
        falha += 1;
        const errorMessage = error.response?.data?.message || error.message;
        console.error(`Erro ao emitir NFSe do lancamento ${lancamento.id}:`, errorMessage);
      } finally {
        setProgressoMassa({
          total: elegiveis.length,
          processados: i + 1,
          sucesso,
          falha,
        });
      }
    }

    if (selectedTurmaId) {
      await loadLancamentos(selectedTurmaId);
    }

    setEmitindoMassa(false);

    if (falha > 0) {
      toast.error(`Emissao em massa concluida com falhas. Sucesso: ${sucesso}, Falhas: ${falha}`);
    } else {
      toast.success(`Emissao em massa concluida. ${sucesso} NFSe emitida(s)`);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pago':
        return '#27ae60';
      case 'pendente':
        return '#f39c12';
      case 'atrasado':
        return '#e74c3c';
      case 'cancelado':
        return '#95a5a6';
      default:
        return '#34495e';
    }
  };

  return (
    <Layout title="Mensalidades">
      <div className={styles.container}>

        {/* Abas das Turmas */}
        <div className={styles.tabsContainer}>
          {turmas.map((turma) => (
            <button
              key={turma.id}
              className={`${styles.tab} ${selectedTurmaId === turma.id ? styles.activeTab : ''}`}
              onClick={() => setSelectedTurmaId(turma.id)}
            >
              {turma.nome}
            </button>
          ))}
        </div>

        <div className={styles.bulkActions}>
          <button
            className={styles.bulkEmitButton}
            onClick={handleEmitirNFSeEmMassa}
            disabled={emitindoMassa || emitindoTodasTurmas || loading}
          >
            {emitindoMassa ? 'Processando fila de NFSe...' : 'Emitir NFSe em massa'}
          </button>
          <button
            className={styles.bulkEmitAllButton}
            onClick={handleEmitirNFSeTodasTurmas}
            disabled
          >
            {emitindoTodasTurmas ? 'Emitindo todas as turmas...' : 'Emitir NFSe — todas as turmas'}
          </button>
          {progressoMassa && (
            <div className={styles.bulkProgress}>
              {progressoMassa.processados}/{progressoMassa.total} processados - sucesso:{' '}
              {progressoMassa.sucesso} - falhas: {progressoMassa.falha}
            </div>
          )}
        </div>

        {loading ? (
          <div className={styles.loading}>Carregando...</div>
        ) : lancamentos.length === 0 ? (
          <div className={styles.empty}>Nenhum lançamento encontrado</div>
        ) : (
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.colAluno}>Aluno</th>
                  {lancamentos[0]?.lancamentos.map((l, idx) => (
                    <th key={idx} className={styles.colPartitura}>
                      <div className={styles.parcelaHeader}>
                        <div className={styles.parcelaTitle}>{l.descricao}</div>
                        <div className={styles.parcelaSubheader}>
                          <div>Status</div>
                          <div>Nº NFSE</div>
                        </div>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {lancamentos.map((alunoLanc) => (
                  <tr key={alunoLanc.alunoId}>
                    <td className={styles.colAluno}>
                      <strong
                        title={alunoLanc.responsavelNome
                          ? `Responsável: ${alunoLanc.responsavelNome}`
                          : 'Responsável financeiro não informado'}
                      >
                        {alunoLanc.alunoNome}
                      </strong>
                    </td>
                    {alunoLanc.lancamentos.map((l) => (
                      <td key={l.id} className={styles.colPartitura}>
                        <div className={styles.valorParcela}>
                          R$ {Number(l.valorFinal).toFixed(2).replace('.', ',')}
                        </div>
                        <div className={styles.parcelaContent}>
                          <select
                            className={styles.statusSelect}
                            value={l.status}
                            onChange={(e) => handleStatusChange(l.id, e.target.value)}
                            disabled={updatingLancamentos.has(l.id)}
                            style={{
                              borderLeftColor: getStatusColor(l.status),
                            }}
                          >
                            <option value="pendente">Pendente</option>
                            <option value="pago">Pago</option>
                            <option value="atrasado">Atrasado</option>
                            <option value="cancelado">Cancelado</option>
                          </select>

                          {l.numeroNFSE ? (
                            <div className={styles.nfseInfoContainer}>
                              <span className={styles.nfseLink} onClick={() => handleOpenModal(l)}>
                                Nº {l.numeroNFSE}
                                <span className={styles.infoIcon}>ℹ️</span>
                              </span>
                            </div>
                          ) : (
                            <div className={styles.nfseInfoContainer}>
                              <span className={styles.nfseLink} onClick={() => handleOpenModal(l)}>
                                Inserir NFSe
                                <span className={styles.infoIcon}>➕</span>
                              </span>
                            </div>
                          )}

                          {l.status === 'pago' && !l.numeroNFSE && (
                            <button
                              className={styles.emitirButton}
                              onClick={() => handleEmitirNFSe(l.id)}
                              disabled={emitindoNFSe.has(l.id)}
                            >
                              {emitindoNFSe.has(l.id) ? 'Emitindo...' : 'Emitir NFSe'}
                            </button>
                          )}
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modalData.lancamento && (
        <NFSeDetailsModal
          isOpen={modalData.isOpen}
          onClose={handleCloseModal}
          lancamentoData={modalData.lancamento}
          isAdmin={isAdmin}
          onCancelNFSe={handleCancelNFSe}
          onUpdateNFSe={handleUpdateNFSe}
        />
      )}
    </Layout>
  );
}
